import { useMutation, useQueryClient } from "@tanstack/react-query";

import { careplanQueryOptions, getCartLines } from "@/domains/careplan/factories";
import { cartCreateMutation } from "@/domains/careplan/models/graphql";
import { storefrontApi } from "@/modules/network";
import { useCartStore } from "@/stores";
import type {
  Cart,
  CartCreateMutation,
  CartCreateMutationVariables,
} from "@/types/storefront-api.gen";

/** Lets the product cards see a failed cart creation and stop waiting for prices. */
export const CREATE_CAREPLAN_CART_MUTATION_KEY = ["careplan", "cart", "create"] as const;

export const useCreateCareplanCart = () => {
  const queryClient = useQueryClient();
  const setRecommendedCartLines = useCartStore((state) => {
    return state.setRecommendedCartLines;
  });

  const saveNewCartToClientStorage = (newCart: Cart) => {
    setRecommendedCartLines(getCartLines(newCart));
    sessionStorage.setItem("careplanCartId", newCart.id);
  };

  return useMutation({
    mutationKey: CREATE_CAREPLAN_CART_MUTATION_KEY,
    mutationFn: createCareplanCart,
    onSuccess: async (response) => {
      const newCart = response?.cartCreate?.cart as Cart;

      if (!newCart) {
        return;
      }

      saveNewCartToClientStorage(newCart);

      await queryClient.invalidateQueries({
        queryKey: careplanQueryOptions.cart.queryKey,
      });
    },
  });
};

const createCareplanCart = async (variables: CartCreateMutationVariables) => {
  const response = await storefrontApi.request<CartCreateMutation>(cartCreateMutation, {
    variables,
  });

  /* Shopify reports a rejected cart as user errors on a 200, so no cart is a failure too. */
  if (response.errors || !response.data?.cartCreate?.cart) {
    const userError = response.data?.cartCreate?.userErrors?.[0]?.message;

    throw new Error(response.errors?.message ?? userError ?? "The cart could not be created");
  }

  return response.data;
};
